import { useState } from "react";
import uploadImage, { UploadOptions } from "@/lib/uploadImage";

export interface UploadResult {
  secureUrl: string;
  publicId: string;
  width: number;
  height: number;
  format: string;
  bytes: number;
}

export interface UsePhotoUploadProps {
  onUploadSuccess: (result: UploadResult | UploadResult[]) => void | Promise<void>;
  onUploadError?: (error: Error) => void;
  uploadOptions: UploadOptions;
}

export const usePhotoUpload = ({ onUploadSuccess, onUploadError, uploadOptions }: UsePhotoUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);

  const uploadPhoto = async () => {
    setIsUploading(true);

    try {
      // Validate required options
      if (!uploadOptions.cloudName || !uploadOptions.uploadPreset) {
        throw new Error("cloudName and uploadPreset are required");
      }

      const result = await uploadImage(uploadOptions);

      // Call the success callback
      await onUploadSuccess(result);
    } catch (error) {
      if (error instanceof Error && error.message === "Upload cancelled by user") {
        // User cancelled the upload, no need to show error
        return;
      }

      // Call the error callback if provided
      if (onUploadError && error instanceof Error) {
        onUploadError(error);
      } else {
        console.error("Photo upload failed:", error);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadPhoto,
    isUploading
  };
};
